import { create } from 'zustand';
import type {
  User,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  ValidateResponse,
  BitrixSsoResponse,
} from '@tailorcast/shared-types';
import { AUTH } from '@shared/api/endpoints';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  loginWithBitrixSso: (accessToken: string, domain: string) => Promise<void>;
  logout: () => void;
  refreshTokens: () => Promise<boolean>;
  validateSession: () => Promise<void>;
}

const STORAGE_TOKEN_KEY = 'sc_token';
const STORAGE_REFRESH_KEY = 'sc_refresh_token';
const STORAGE_USER_KEY = 'sc_user';
const STORAGE_CONTEXT_KEY = 'sc_context';

function loadFromStorage() {
  try {
    const token = localStorage.getItem(STORAGE_TOKEN_KEY);
    const refreshToken = localStorage.getItem(STORAGE_REFRESH_KEY);
    const userJson = localStorage.getItem(STORAGE_USER_KEY);
    const user = userJson ? (JSON.parse(userJson) as User) : null;
    return { token, refreshToken, user };
  } catch {
    return { token: null, refreshToken: null, user: null };
  }
}

function saveToStorage(token: string, refreshToken: string, user: User) {
  localStorage.setItem(STORAGE_TOKEN_KEY, token);
  localStorage.setItem(STORAGE_REFRESH_KEY, refreshToken);
  localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(user));
}

function clearStorage() {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
  localStorage.removeItem(STORAGE_REFRESH_KEY);
  localStorage.removeItem(STORAGE_USER_KEY);
  localStorage.removeItem(STORAGE_CONTEXT_KEY);
}

const stored = loadFromStorage();

export const useAuthStore = create<AuthState>((set, get) => ({
  token: stored.token,
  refreshToken: stored.refreshToken,
  user: stored.user,
  isAuthenticated: !!stored.token,
  isLoading: !!stored.token,

  login: async (credentials: LoginRequest) => {
    const res = await fetch(AUTH.LOGIN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? 'Login failed');
    }

    const data: LoginResponse = await res.json();
    saveToStorage(data.token, data.refreshToken, data.user);
    set({
      token: data.token,
      refreshToken: data.refreshToken,
      user: data.user,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  loginWithBitrixSso: async (accessToken: string, domain: string) => {
    const res = await fetch(AUTH.BITRIX_SSO, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken, domain }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? 'Bitrix SSO failed');
    }

    const data: BitrixSsoResponse = await res.json();
    const user: User = {
      id: data.user.id,
      email: data.user.email,
      name: [data.user.firstName, data.user.lastName].filter(Boolean).join(' '),
      role: data.user.role,
    };
    saveToStorage(data.token, data.refreshToken, user);
    localStorage.setItem(STORAGE_CONTEXT_KEY, 'bitrix24');
    set({
      token: data.token,
      refreshToken: data.refreshToken,
      user,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  logout: () => {
    clearStorage();
    set({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  refreshTokens: async () => {
    const { refreshToken } = get();
    if (!refreshToken) return false;

    try {
      const res = await fetch(AUTH.REFRESH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) return false;

      const data: RefreshTokenResponse = await res.json();
      const { user } = get();
      if (user) saveToStorage(data.token, data.refreshToken, user);
      set({ token: data.token, refreshToken: data.refreshToken });
      return true;
    } catch {
      return false;
    }
  },

  validateSession: async () => {
    const { token, refreshTokens, logout } = get();
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      const res = await fetch(AUTH.VALIDATE, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data: ValidateResponse = await res.json();
        set({ user: data.user, isAuthenticated: true, isLoading: false });
        return;
      }

      if (res.status === 401) {
        const refreshed = await refreshTokens();
        if (refreshed) {
          set({ isAuthenticated: true, isLoading: false });
          return;
        }
      }

      logout();
    } catch {
      logout();
    }
  },
}));
